import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  CheckCircle, 
  XCircle, 
  Users, 
  House, 
  CalendarBlank, 
  CurrencyDollar,
  ShieldCheck,
  Warning,
  Trash,
  Eye,
  UserCircleMinus,
  UserCirclePlus,
  CaretRight,
  ChartLine,
  Gear,
  Calculator,
  Wallet,
  ArrowsClockwise,
  Tag,
  Plus,
  PencilSimple,
  Archive,
  EyeSlash,
  ProhibitInset,
  Flag,
  NotePencil,
  Clock,
  Handshake
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { token } = useAuth();
  const [listings, setListings] = useState([]);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [feeConfig, setFeeConfig] = useState(null);
  const [platformRevenue, setPlatformRevenue] = useState(null);
  const [pendingPayouts, setPendingPayouts] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [testPrice, setTestPrice] = useState(25);
  const [feePreview, setFeePreview] = useState(null);
  
  // Category management state
  const [categories, setCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    slug: '',
    description: '',
    minimum_hourly_price: 15,
    icon: 'house',
    active: true,
    visible: true,
    sort_order: 1
  });

  // Cancellation monitoring state
  const [cancellationStats, setCancellationStats] = useState([]);
  const [cancellationFilter, setCancellationFilter] = useState('all'); // 'all', 'host', 'guest'
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedUserForNotes, setSelectedUserForNotes] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [listingsRes, usersRes, bookingsRes, statsRes, verificationsRes, feeConfigRes, revenueRes, payoutsRes, categoriesRes, cancellationStatsRes] = await Promise.all([
        adminApi.getAllListings(token),
        adminApi.getAllUsers(token),
        adminApi.getAllBookings(token),
        adminApi.getStats(token),
        adminApi.getVerifications(token).catch(() => ({ data: [] })),
        adminApi.getFeeConfig(token).catch(() => ({ data: null })),
        adminApi.getPlatformRevenue(token).catch(() => ({ data: null })),
        adminApi.getPendingPayouts(token).catch(() => ({ data: null })),
        adminApi.getCategories(token).catch(() => ({ data: [] })),
        adminApi.getCancellationStats(null, false, token).catch(() => ({ data: [] })),
      ]);
      setListings(listingsRes.data);
      setUsers(usersRes.data);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
      setVerifications(verificationsRes.data || []);
      setFeeConfig(feeConfigRes.data || null);
      setPlatformRevenue(revenueRes.data || null);
      setPendingPayouts(payoutsRes.data || null);
      setCategories(categoriesRes.data || []);
      setCancellationStats(cancellationStatsRes.data || []);
    } catch (error) {
      console.error('Failed to load admin data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveListing = async (listingId) => {
    try {
      await adminApi.approveListing(listingId, token);
      toast.success('Listing approved');
      loadData();
    } catch (error) {
      toast.error('Failed to approve listing');
    }
  };

  const handleRejectListing = async (listingId) => {
    try {
      await adminApi.rejectListing(listingId, token);
      toast.success('Listing rejected');
      loadData();
    } catch (error) {
      toast.error('Failed to reject listing');
    }
  };

  const handleRemoveListing = async (listingId) => {
    if (!window.confirm('Are you sure you want to remove this listing?')) return;
    try {
      await adminApi.removeListing(listingId, token);
      toast.success('Listing removed');
      loadData();
    } catch (error) {
      toast.error('Failed to remove listing');
    }
  };

  const handleSuspendUser = async (userId) => {
    if (!window.confirm('Are you sure you want to suspend this user?')) return;
    try {
      await adminApi.suspendUser(userId, token);
      toast.success('User suspended');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend user');
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await adminApi.activateUser(userId, token);
      toast.success('User activated');
      loadData();
    } catch (error) {
      toast.error('Failed to activate user');
    }
  };

  const handleApproveVerification = async (verificationId) => {
    try {
      await adminApi.approveVerification(verificationId, token);
      toast.success('Verification approved');
      loadData();
    } catch (error) {
      toast.error('Failed to approve verification');
    }
  };

  const handleRejectVerification = async (verificationId) => {
    try {
      await adminApi.rejectVerification(verificationId, token);
      toast.success('Verification rejected');
      loadData();
    } catch (error) {
      toast.error('Failed to reject verification');
    }
  };

  // Fee configuration handlers
  const handlePreviewFee = async () => {
    try {
      const res = await adminApi.previewFeeCalculation(testPrice, token);
      setFeePreview(res.data);
    } catch (error) {
      toast.error('Failed to preview fee calculation');
    }
  };

  const handleUpdateFeeConfig = async (updatedConfig) => {
    try {
      await adminApi.updateFeeConfig(updatedConfig, token);
      toast.success('Fee configuration updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update fee configuration');
    }
  };

  const handleResetFeeConfig = async () => {
    if (!window.confirm('Are you sure you want to reset fee configuration to defaults?')) return;
    try {
      await adminApi.resetFeeConfig(token);
      toast.success('Fee configuration reset to defaults');
      loadData();
    } catch (error) {
      toast.error('Failed to reset fee configuration');
    }
  };

  const handleToggleFeeEnabled = async () => {
    try {
      await adminApi.updateFeeConfig({ enabled: !feeConfig?.enabled }, token);
      toast.success(feeConfig?.enabled ? 'Fee system disabled' : 'Fee system enabled');
      loadData();
    } catch (error) {
      toast.error('Failed to toggle fee system');
    }
  };

  // Category management handlers
  const generateSlug = (name) => {
    return name.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        name: category.name,
        slug: category.slug,
        description: category.description || '',
        minimum_hourly_price: category.minimum_hourly_price,
        icon: category.icon || 'house',
        active: category.active,
        visible: category.visible,
        sort_order: category.sort_order
      });
    } else {
      setEditingCategory(null);
      setCategoryForm({
        name: '',
        slug: '',
        description: '',
        minimum_hourly_price: 15,
        icon: 'house',
        active: true,
        visible: true,
        sort_order: categories.length + 1
      });
    }
    setShowCategoryModal(true);
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setEditingCategory(null);
    setCategoryForm({
      name: '',
      slug: '',
      description: '',
      minimum_hourly_price: 15,
      icon: 'house',
      active: true,
      visible: true,
      sort_order: 1
    });
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    
    if (!categoryForm.name || !categoryForm.slug || categoryForm.minimum_hourly_price <= 0) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editingCategory) {
        await adminApi.updateCategory(editingCategory.id, categoryForm, token);
        toast.success('Category updated successfully');
      } else {
        await adminApi.createCategory(categoryForm, token);
        toast.success('Category created successfully');
      }
      closeCategoryModal();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    }
  };

  const handleDeleteCategory = async (category) => {
    setCategoryToDelete(category);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete) return;
    
    try {
      const res = await adminApi.deleteCategory(categoryToDelete.id, token);
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete category');
    } finally {
      setShowDeleteConfirm(false);
      setCategoryToDelete(null);
    }
  };

  const cancelDeleteCategory = () => {
    setShowDeleteConfirm(false);
    setCategoryToDelete(null);
  };

  const handleToggleCategoryActive = async (category) => {
    try {
      await adminApi.updateCategory(category.id, { active: !category.active }, token);
      toast.success(category.active ? 'Category deactivated' : 'Category activated');
      loadData();
    } catch (error) {
      toast.error('Failed to toggle category status');
    }
  };

  const handleToggleCategoryVisible = async (category) => {
    try {
      await adminApi.updateCategory(category.id, { visible: !category.visible }, token);
      toast.success(category.visible ? 'Category hidden' : 'Category visible');
      loadData();
    } catch (error) {
      toast.error('Failed to toggle category visibility');
    }
  };

  // Cancellation monitoring handlers
  const loadCancellationStats = async () => {
    try {
      const userType = cancellationFilter === 'all' ? null : cancellationFilter;
      const res = await adminApi.getCancellationStats(userType, flaggedOnly, token);
      setCancellationStats(res.data || []);
    } catch (error) {
      toast.error('Failed to load cancellation stats');
    }
  };

  useEffect(() => {
    if (activeTab === 'cancellations') {
      loadCancellationStats();
    }
  }, [cancellationFilter, flaggedOnly, activeTab]);

  const handleRemoveFlag = async (userId) => {
    try {
      await adminApi.removeUserFlag(userId, token);
      toast.success('Flag removed successfully');
      loadCancellationStats();
    } catch (error) {
      toast.error('Failed to remove flag');
    }
  };

  const openNotesModal = (user) => {
    setSelectedUserForNotes(user);
    setAdminNotes(user.admin_notes || '');
    setShowNotesModal(true);
  };

  const handleSaveNotes = async () => {
    if (!selectedUserForNotes) return;
    try {
      await adminApi.addUserNotes(selectedUserForNotes.user_id, adminNotes, token);
      toast.success('Notes saved successfully');
      setShowNotesModal(false);
      loadCancellationStats();
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const pendingListings = listings.filter(l => !l.is_approved && l.is_active);
  const pendingVerifications = verifications.filter(v => v.status === 'pending');
  const flaggedUsers = cancellationStats.filter(s => s.flag_status === 'flagged');

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-zinc-400">Loading admin dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] py-8">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-serif text-white">Admin Dashboard</h1>
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
            System Online
          </div>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 mb-8">
          <div className="bg-[#121212] border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <Users size={24} className="text-blue-400" weight="duotone" />
              <span className="text-xs text-zinc-500">Total</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats?.total_users || 0}</p>
            <p className="text-zinc-500 text-sm">Users</p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <House size={24} className="text-[#D4AF37]" weight="duotone" />
              <span className="text-xs text-zinc-500">Active</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats?.total_listings || 0}</p>
            <p className="text-zinc-500 text-sm">Listings</p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <CalendarBlank size={24} className="text-green-400" weight="duotone" />
              <span className="text-xs text-zinc-500">Total</span>
            </div>
            <p className="text-2xl font-bold text-white">{stats?.total_bookings || 0}</p>
            <p className="text-zinc-500 text-sm">Bookings</p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <CurrencyDollar size={24} className="text-emerald-400" weight="duotone" />
              <span className="text-xs text-zinc-500">Revenue</span>
            </div>
            <p className="text-2xl font-bold text-white">${stats?.total_revenue?.toFixed(2) || '0.00'}</p>
            <p className="text-zinc-500 text-sm">Total</p>
          </div>
          
          <div className="bg-[#121212] border border-white/10 p-5">
            <div className="flex items-center justify-between mb-3">
              <Warning size={24} className="text-yellow-400" weight="duotone" />
              <span className="text-xs text-zinc-500">Pending</span>
            </div>
            <p className="text-2xl font-bold text-[#D4AF37]">{stats?.pending_listings || 0}</p>
            <p className="text-zinc-500 text-sm">Approvals</p>
          </div>
        </div>

        {/* Quick Actions / Alerts */}
        {(pendingListings.length > 0 || pendingVerifications.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            {pendingListings.length > 0 && (
              <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Warning size={24} className="text-yellow-400" />
                  <div>
                    <p className="text-yellow-400 font-semibold">{pendingListings.length} Listings Pending Approval</p>
                    <p className="text-yellow-400/70 text-sm">Review and approve new listings</p>
                  </div>
                </div>
                <Button 
                  onClick={() => setActiveTab('listings')}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black"
                  size="sm"
                >
                  Review
                </Button>
              </div>
            )}
            
            {pendingVerifications.length > 0 && (
              <div className="bg-blue-500/10 border border-blue-500/30 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ShieldCheck size={24} className="text-blue-400" />
                  <div>
                    <p className="text-blue-400 font-semibold">{pendingVerifications.length} ID Verifications Pending</p>
                    <p className="text-blue-400/70 text-sm">Review user identity documents</p>
                  </div>
                </div>
                <Button 
                  onClick={() => setActiveTab('verifications')}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                  size="sm"
                >
                  Review
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Affiliate Management Quick Access */}
        <div className="mb-8">
          <Link 
            to="/admin/affiliates"
            data-testid="admin-affiliates-link"
            className="block bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4 hover:bg-[#D4AF37]/20 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Handshake size={24} className="text-[#D4AF37]" />
                <div>
                  <p className="text-[#D4AF37] font-semibold">Affiliate Program Management</p>
                  <p className="text-[#D4AF37]/70 text-sm">Review applications, manage affiliates, track commissions</p>
                </div>
              </div>
              <CaretRight size={20} className="text-[#D4AF37]" />
            </div>
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-white/10 overflow-x-auto">
          {['overview', 'listings', 'users', 'bookings', 'verifications', 'categories', 'cancellations', 'disputes', 'fees'].map((tab) => (
            <button
              key={tab}
              data-testid={`admin-tab-${tab}`}
              onClick={() => setActiveTab(tab)}
              className={`pb-4 px-4 transition-colors whitespace-nowrap capitalize ${
                activeTab === tab
                  ? 'text-[#D4AF37] border-b-2 border-[#D4AF37]'
                  : 'text-zinc-500 hover:text-white'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-[#121212] border border-white/10 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <ChartLine size={24} className="text-[#D4AF37]" />
                  <h3 className="text-lg font-semibold text-white">This Month</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">New Users</span>
                    <span className="text-white font-semibold">{stats?.new_users_this_month || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Bookings</span>
                    <span className="text-white font-semibold">{stats?.bookings_this_month || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Active Hosts</span>
                    <span className="text-white font-semibold">{stats?.active_hosts || 0}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#121212] border border-white/10 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Users size={24} className="text-blue-400" />
                  <h3 className="text-lg font-semibold text-white">User Status</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Total Users</span>
                    <span className="text-white font-semibold">{stats?.total_users || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Suspended</span>
                    <span className="text-red-400 font-semibold">{stats?.suspended_users || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Pending Verifications</span>
                    <span className="text-yellow-400 font-semibold">{stats?.pending_verifications || 0}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-[#121212] border border-white/10 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <House size={24} className="text-green-400" />
                  <h3 className="text-lg font-semibold text-white">Listings</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Total Listings</span>
                    <span className="text-white font-semibold">{stats?.total_listings || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Pending Approval</span>
                    <span className="text-yellow-400 font-semibold">{stats?.pending_listings || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Recent Bookings</h3>
              {bookings.slice(0, 5).length === 0 ? (
                <p className="text-zinc-500">No recent bookings</p>
              ) : (
                <div className="space-y-3">
                  {bookings.slice(0, 5).map((booking) => (
                    <div key={booking.id} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                      <div>
                        <p className="text-white">{booking.listing_title}</p>
                        <p className="text-zinc-500 text-sm">{booking.guest_name} → {booking.host_name}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[#D4AF37] font-semibold">${booking.total_price}</p>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          booking.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          booking.status === 'approved' ? 'bg-blue-500/20 text-blue-400' :
                          booking.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-zinc-500/20 text-zinc-400'
                        }`}>
                          {booking.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Listings Tab */}
        {activeTab === 'listings' && (
          <div>
            {pendingListings.length > 0 && (
              <div className="mb-8">
                <h3 className="text-xl text-[#D4AF37] mb-4">Pending Approval ({pendingListings.length})</h3>
                <div className="space-y-4">
                  {pendingListings.map((listing) => (
                    <div key={listing.id} data-testid={`pending-listing-${listing.id}`} className="bg-[#121212] border border-yellow-500/30 p-6">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="text-xl font-serif text-white mb-2">{listing.title}</h4>
                          <p className="text-zinc-500 mb-2">Host: {listing.host_name}</p>
                          <p className="text-zinc-600 text-sm mb-3">
                            {listing.location_city} • {listing.category} • ${listing.price_per_hour}/hour
                          </p>
                          <p className="text-zinc-400 text-sm line-clamp-2">{listing.description}</p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            data-testid={`approve-listing-${listing.id}`}
                            onClick={() => handleApproveListing(listing.id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            size="sm"
                          >
                            <CheckCircle size={18} className="mr-1" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleRejectListing(listing.id)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            size="sm"
                          >
                            <XCircle size={18} className="mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <h3 className="text-xl text-white mb-4">All Listings ({listings.length})</h3>
            <div className="overflow-x-auto bg-[#121212] border border-white/10">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Title</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Host</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Category</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Price</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Status</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {listings.map((listing) => (
                    <tr key={listing.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4 text-white">{listing.title}</td>
                      <td className="py-3 px-4 text-zinc-400">{listing.host_name}</td>
                      <td className="py-3 px-4 text-zinc-400 capitalize">{listing.category?.replace('_', ' ')}</td>
                      <td className="py-3 px-4 text-zinc-400">${listing.price_per_hour}/hr</td>
                      <td className="py-3 px-4">
                        {!listing.is_active ? (
                          <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded">Removed</span>
                        ) : listing.is_approved ? (
                          <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">Active</span>
                        ) : (
                          <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-400 rounded">Pending</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2">
                          {listing.is_active && (
                            <button
                              onClick={() => handleRemoveListing(listing.id)}
                              className="text-red-400 hover:text-red-300"
                              title="Remove listing"
                            >
                              <Trash size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <h2 className="text-xl font-serif text-white mb-6">All Users ({users.length})</h2>
            <div className="overflow-x-auto bg-[#121212] border border-white/10">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Name</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Email</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Role</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Host</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Verified</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Status</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4 text-white">{user.name}</td>
                      <td className="py-3 px-4 text-zinc-400">{user.email}</td>
                      <td className="py-3 px-4">
                        <span className={`text-xs px-2 py-1 rounded ${
                          user.role === 'admin' ? 'bg-purple-500/20 text-purple-400' :
                          user.role === 'host' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-zinc-500/20 text-zinc-400'
                        }`}>
                          {user.role?.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {user.is_host ? (
                          <span className="text-green-400">Yes</span>
                        ) : (
                          <span className="text-zinc-500">No</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          <span className={`w-2 h-2 rounded-full ${user.email_verified ? 'bg-green-400' : 'bg-zinc-600'}`} title="Email"></span>
                          <span className={`w-2 h-2 rounded-full ${user.phone_verified ? 'bg-green-400' : 'bg-zinc-600'}`} title="Phone"></span>
                          <span className={`w-2 h-2 rounded-full ${user.id_verified ? 'bg-green-400' : 'bg-zinc-600'}`} title="ID"></span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {user.is_suspended ? (
                          <span className="text-xs px-2 py-1 bg-red-500/20 text-red-400 rounded">Suspended</span>
                        ) : (
                          <span className="text-xs px-2 py-1 bg-green-500/20 text-green-400 rounded">Active</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {user.role !== 'admin' && (
                          user.is_suspended ? (
                            <button
                              onClick={() => handleActivateUser(user.id)}
                              className="text-green-400 hover:text-green-300"
                              title="Activate user"
                            >
                              <UserCirclePlus size={20} />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleSuspendUser(user.id)}
                              className="text-red-400 hover:text-red-300"
                              title="Suspend user"
                            >
                              <UserCircleMinus size={20} />
                            </button>
                          )
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div>
            <h2 className="text-xl font-serif text-white mb-6">All Bookings ({bookings.length})</h2>
            <div className="overflow-x-auto bg-[#121212] border border-white/10">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Listing</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Guest</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Host</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Date</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Amount</th>
                    <th className="text-left py-3 px-4 text-zinc-500 font-normal">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking) => (
                    <tr key={booking.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4 text-white">{booking.listing_title}</td>
                      <td className="py-3 px-4 text-zinc-400">{booking.guest_name}</td>
                      <td className="py-3 px-4 text-zinc-400">{booking.host_name}</td>
                      <td className="py-3 px-4 text-zinc-400">{booking.booking_date}</td>
                      <td className="py-3 px-4 text-[#D4AF37] font-semibold">${booking.total_price}</td>
                      <td className="py-3 px-4">
                        <span className={`text-xs px-2 py-1 border rounded ${
                          booking.status === 'completed' ? 'bg-green-600/20 text-green-400 border-green-600/30' :
                          booking.status === 'approved' ? 'bg-blue-600/20 text-blue-400 border-blue-600/30' :
                          booking.status === 'pending' ? 'bg-yellow-600/20 text-yellow-400 border-yellow-600/30' :
                          booking.status === 'rejected' ? 'bg-red-600/20 text-red-400 border-red-600/30' :
                          'bg-zinc-600/20 text-zinc-400 border-zinc-600/30'
                        }`}>
                          {booking.status?.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Verifications Tab */}
        {activeTab === 'verifications' && (
          <div>
            <h2 className="text-xl font-serif text-white mb-6">ID Verifications</h2>
            
            {pendingVerifications.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg text-[#D4AF37] mb-4">Pending Review ({pendingVerifications.length})</h3>
                <div className="space-y-4">
                  {pendingVerifications.map((verification) => (
                    <div key={verification.id} className="bg-[#121212] border border-yellow-500/30 p-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white font-semibold">{verification.user_name}</p>
                          <p className="text-zinc-500 text-sm">{verification.user_email}</p>
                          <p className="text-zinc-400 text-sm mt-2">
                            ID Type: <span className="capitalize">{verification.id_type?.replace('_', ' ')}</span>
                          </p>
                          <p className="text-zinc-600 text-xs mt-1">
                            Submitted: {new Date(verification.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleApproveVerification(verification.id)}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            size="sm"
                          >
                            <CheckCircle size={18} className="mr-1" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleRejectVerification(verification.id)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                            size="sm"
                          >
                            <XCircle size={18} className="mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <h3 className="text-lg text-white mb-4">All Verifications</h3>
            {verifications.length === 0 ? (
              <div className="text-center py-12 bg-[#121212] border border-white/10">
                <ShieldCheck size={48} className="mx-auto text-zinc-600 mb-4" />
                <p className="text-zinc-500">No verification requests yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto bg-[#121212] border border-white/10">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal">User</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal">ID Type</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal">Submitted</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {verifications.map((v) => (
                      <tr key={v.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-3 px-4">
                          <p className="text-white">{v.user_name}</p>
                          <p className="text-zinc-500 text-sm">{v.user_email}</p>
                        </td>
                        <td className="py-3 px-4 text-zinc-400 capitalize">{v.id_type?.replace('_', ' ')}</td>
                        <td className="py-3 px-4 text-zinc-400">{new Date(v.created_at).toLocaleDateString()}</td>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-1 rounded ${
                            v.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                            v.status === 'rejected' ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {v.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Categories Tab */}
        {activeTab === 'categories' && (
          <div className="space-y-6">
            {/* Header with Add Button */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-serif text-white mb-1">Category Management</h2>
                <p className="text-zinc-500 text-sm">Manage listing categories and minimum hourly prices</p>
              </div>
              <Button
                data-testid="add-category-btn"
                onClick={() => openCategoryModal()}
                className="bg-[#D4AF37] hover:bg-[#c9a032] text-black"
              >
                <Plus size={18} className="mr-2" />
                Add Category
              </Button>
            </div>

            {/* Categories Table */}
            <div className="bg-[#121212] border border-white/10 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 bg-[#0a0a0a]">
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">Order</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">Category</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">Slug</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">Min Price/hr</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">Listings</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">Active</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">Visible</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((category) => (
                      <tr key={category.id} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-3 px-4 text-zinc-400">{category.sort_order}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded bg-[#D4AF37]/10 flex items-center justify-center">
                              <Tag size={16} className="text-[#D4AF37]" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{category.name}</p>
                              {category.description && (
                                <p className="text-zinc-500 text-xs truncate max-w-[200px]">{category.description}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-zinc-400 font-mono text-sm">{category.slug}</td>
                        <td className="py-3 px-4 text-[#D4AF37] font-semibold">${category.minimum_hourly_price}</td>
                        <td className="py-3 px-4 text-zinc-400">{category.listings_count || 0}</td>
                        <td className="py-3 px-4">
                          <button
                            data-testid={`toggle-active-${category.slug}`}
                            onClick={() => handleToggleCategoryActive(category)}
                            className={`px-2 py-1 text-xs rounded-full transition-colors ${
                              category.active
                                ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                                : 'bg-red-500/20 text-red-400 border border-red-500/30'
                            }`}
                          >
                            {category.active ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="py-3 px-4">
                          <button
                            data-testid={`toggle-visible-${category.slug}`}
                            onClick={() => handleToggleCategoryVisible(category)}
                            className={`px-2 py-1 text-xs rounded-full transition-colors ${
                              category.visible
                                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                : 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30'
                            }`}
                          >
                            {category.visible ? 'Visible' : 'Hidden'}
                          </button>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              data-testid={`edit-category-${category.slug}`}
                              onClick={() => openCategoryModal(category)}
                              className="p-2 hover:bg-white/10 rounded transition-colors text-zinc-400 hover:text-white"
                              title="Edit"
                            >
                              <PencilSimple size={16} />
                            </button>
                            <button
                              data-testid={`delete-category-${category.slug}`}
                              onClick={() => handleDeleteCategory(category)}
                              className="p-2 hover:bg-red-500/10 rounded transition-colors text-zinc-400 hover:text-red-400"
                              title={category.listings_count > 0 ? 'Archive' : 'Delete'}
                            >
                              {category.listings_count > 0 ? <Archive size={16} /> : <Trash size={16} />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {categories.length === 0 && (
                <div className="text-center py-12">
                  <Tag size={48} className="mx-auto text-zinc-600 mb-4" />
                  <p className="text-zinc-500">No categories found</p>
                  <Button
                    onClick={() => openCategoryModal()}
                    className="mt-4 bg-[#D4AF37] hover:bg-[#c9a032] text-black"
                  >
                    Create First Category
                  </Button>
                </div>
              )}
            </div>

            {/* Category Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-[#121212] border border-white/10 p-4">
                <div className="flex items-center gap-3">
                  <Tag size={20} className="text-[#D4AF37]" />
                  <div>
                    <p className="text-zinc-500 text-sm">Total Categories</p>
                    <p className="text-white text-xl font-semibold">{categories.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-[#121212] border border-white/10 p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle size={20} className="text-green-400" />
                  <div>
                    <p className="text-zinc-500 text-sm">Active Categories</p>
                    <p className="text-white text-xl font-semibold">{categories.filter(c => c.active).length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-[#121212] border border-white/10 p-4">
                <div className="flex items-center gap-3">
                  <Eye size={20} className="text-blue-400" />
                  <div>
                    <p className="text-zinc-500 text-sm">Visible to Hosts</p>
                    <p className="text-white text-xl font-semibold">{categories.filter(c => c.active && c.visible).length}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Category Modal */}
        {showCategoryModal && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
            <div className="bg-[#121212] border border-white/10 rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-white/10">
                <h3 className="text-xl font-semibold text-white">
                  {editingCategory ? 'Edit Category' : 'Add New Category'}
                </h3>
              </div>
              
              <form onSubmit={handleCategorySubmit} className="p-6 space-y-4">
                <div>
                  <Label className="text-zinc-400 mb-2 block">Category Name *</Label>
                  <Input
                    data-testid="category-name-input"
                    value={categoryForm.name}
                    onChange={(e) => {
                      const name = e.target.value;
                      setCategoryForm(prev => ({
                        ...prev,
                        name,
                        slug: editingCategory ? prev.slug : generateSlug(name)
                      }));
                    }}
                    className="bg-[#0a0a0a] border-white/10 text-white"
                    placeholder="e.g., Home Theater"
                    required
                  />
                </div>

                <div>
                  <Label className="text-zinc-400 mb-2 block">Slug *</Label>
                  <Input
                    data-testid="category-slug-input"
                    value={categoryForm.slug}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, slug: generateSlug(e.target.value) }))}
                    className="bg-[#0a0a0a] border-white/10 text-white font-mono"
                    placeholder="e.g., home-theater"
                    required
                  />
                </div>

                <div>
                  <Label className="text-zinc-400 mb-2 block">Description</Label>
                  <textarea
                    data-testid="category-description-input"
                    value={categoryForm.description}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full bg-[#0a0a0a] border border-white/10 text-white rounded-md p-3 text-sm"
                    placeholder="Brief description of this category..."
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400 mb-2 block">Minimum Price/Hour *</Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                      <Input
                        data-testid="category-min-price-input"
                        type="number"
                        value={categoryForm.minimum_hourly_price}
                        onChange={(e) => setCategoryForm(prev => ({ ...prev, minimum_hourly_price: parseFloat(e.target.value) || 0 }))}
                        className="bg-[#0a0a0a] border-white/10 text-white pl-7"
                        min="1"
                        step="1"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-zinc-400 mb-2 block">Sort Order</Label>
                    <Input
                      data-testid="category-sort-input"
                      type="number"
                      value={categoryForm.sort_order}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 1 }))}
                      className="bg-[#0a0a0a] border-white/10 text-white"
                      min="1"
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-zinc-400 mb-2 block">Icon Name</Label>
                  <Input
                    data-testid="category-icon-input"
                    value={categoryForm.icon}
                    onChange={(e) => setCategoryForm(prev => ({ ...prev, icon: e.target.value }))}
                    className="bg-[#0a0a0a] border-white/10 text-white"
                    placeholder="e.g., house, book, barbell"
                  />
                  <p className="text-zinc-600 text-xs mt-1">Icon identifier for future use</p>
                </div>

                <div className="flex items-center gap-6 pt-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={categoryForm.active}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, active: e.target.checked }))}
                      className="w-4 h-4 rounded border-white/10"
                    />
                    <span className="text-zinc-400 text-sm">Active</span>
                  </label>
                  
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={categoryForm.visible}
                      onChange={(e) => setCategoryForm(prev => ({ ...prev, visible: e.target.checked }))}
                      className="w-4 h-4 rounded border-white/10"
                    />
                    <span className="text-zinc-400 text-sm">Visible to Hosts</span>
                  </label>
                </div>

                <div className="flex gap-3 pt-4 border-t border-white/10">
                  <Button
                    type="button"
                    onClick={closeCategoryModal}
                    variant="outline"
                    className="flex-1 border-white/10 text-zinc-400 hover:text-white"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    data-testid="save-category-btn"
                    className="flex-1 bg-[#D4AF37] hover:bg-[#c9a032] text-black"
                  >
                    {editingCategory ? 'Save Changes' : 'Create Category'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Category Confirmation Modal */}
        {showDeleteConfirm && categoryToDelete && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
            <div className="bg-[#121212] border border-white/10 rounded-lg max-w-md w-full">
              <div className="p-6 border-b border-white/10">
                <h3 className="text-xl font-semibold text-white">
                  {categoryToDelete.listings_count > 0 ? 'Archive Category?' : 'Delete Category?'}
                </h3>
              </div>
              
              <div className="p-6">
                <p className="text-zinc-400 mb-4">
                  {categoryToDelete.listings_count > 0 
                    ? `This category has ${categoryToDelete.listings_count} listing(s). It will be archived (hidden from new listings but existing listings will remain intact).`
                    : `Are you sure you want to delete "${categoryToDelete.name}"? This action cannot be undone.`
                  }
                </p>
                
                <div className="bg-[#0a0a0a] border border-white/5 p-4 rounded mb-4">
                  <p className="text-white font-medium">{categoryToDelete.name}</p>
                  <p className="text-zinc-500 text-sm">Min price: ${categoryToDelete.minimum_hourly_price}/hr</p>
                </div>

                <div className="flex gap-3">
                  <Button
                    data-testid="cancel-delete-btn"
                    onClick={cancelDeleteCategory}
                    variant="outline"
                    className="flex-1 border-white/10 text-zinc-400 hover:text-white"
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="confirm-delete-btn"
                    onClick={confirmDeleteCategory}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    {categoryToDelete.listings_count > 0 ? 'Archive' : 'Delete'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cancellations Tab */}
        {activeTab === 'cancellations' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-serif text-white mb-1">Cancellation Monitoring</h2>
                <p className="text-zinc-500 text-sm">Track cancellation patterns and manage flagged users</p>
              </div>
              
              {/* Filters */}
              <div className="flex gap-3">
                <select
                  value={cancellationFilter}
                  onChange={(e) => setCancellationFilter(e.target.value)}
                  className="bg-[#121212] border border-white/10 text-white px-3 py-2 rounded text-sm"
                >
                  <option value="all">All Users</option>
                  <option value="host">Hosts Only</option>
                  <option value="guest">Guests Only</option>
                </select>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={flaggedOnly}
                    onChange={(e) => setFlaggedOnly(e.target.checked)}
                    className="w-4 h-4 rounded border-white/10"
                  />
                  <span className="text-zinc-400 text-sm">Flagged Only</span>
                </label>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-[#121212] border border-white/10 p-4">
                <div className="flex items-center gap-3">
                  <ProhibitInset size={20} className="text-red-400" />
                  <div>
                    <p className="text-zinc-500 text-sm">Total Cancellations</p>
                    <p className="text-white text-xl font-semibold">
                      {cancellationStats.reduce((acc, s) => acc + s.total_cancellations, 0)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-[#121212] border border-white/10 p-4">
                <div className="flex items-center gap-3">
                  <Flag size={20} className="text-yellow-400" />
                  <div>
                    <p className="text-zinc-500 text-sm">Flagged Users</p>
                    <p className="text-white text-xl font-semibold">{flaggedUsers.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-[#121212] border border-white/10 p-4">
                <div className="flex items-center gap-3">
                  <Clock size={20} className="text-orange-400" />
                  <div>
                    <p className="text-zinc-500 text-sm">&lt;24hr Cancellations</p>
                    <p className="text-white text-xl font-semibold">
                      {cancellationStats.reduce((acc, s) => acc + s.cancellations_within_24hr, 0)}
                    </p>
                  </div>
                </div>
              </div>
              <div className="bg-[#121212] border border-white/10 p-4">
                <div className="flex items-center gap-3">
                  <Users size={20} className="text-blue-400" />
                  <div>
                    <p className="text-zinc-500 text-sm">Users with Cancellations</p>
                    <p className="text-white text-xl font-semibold">{cancellationStats.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Cancellation Stats Table */}
            <div className="bg-[#121212] border border-white/10 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 bg-[#0a0a0a]">
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">User</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">Type</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">Bookings</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">Cancellations</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">&lt;48hr</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">&lt;24hr</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">Last Cancel</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">Status</th>
                      <th className="text-left py-3 px-4 text-zinc-500 font-normal text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cancellationStats.map((stat) => (
                      <tr key={stat.user_id} className={`border-b border-white/5 hover:bg-white/5 ${stat.flag_status === 'flagged' ? 'bg-red-500/5' : ''}`}>
                        <td className="py-3 px-4">
                          <p className="text-white font-medium">{stat.user_name}</p>
                          {stat.listings_count !== undefined && (
                            <p className="text-zinc-500 text-xs">{stat.listings_count} listings</p>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            stat.user_type === 'host' 
                              ? 'bg-purple-500/20 text-purple-400' 
                              : 'bg-blue-500/20 text-blue-400'
                          }`}>
                            {stat.user_type}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-zinc-400">{stat.total_bookings}</td>
                        <td className="py-3 px-4 text-white font-semibold">{stat.total_cancellations}</td>
                        <td className="py-3 px-4 text-orange-400">{stat.cancellations_within_48hr}</td>
                        <td className="py-3 px-4 text-red-400">{stat.cancellations_within_24hr}</td>
                        <td className="py-3 px-4 text-zinc-400">{stat.last_cancellation_date || '-'}</td>
                        <td className="py-3 px-4">
                          {stat.flag_status === 'flagged' ? (
                            <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-400">
                              <Flag size={12} />
                              Flagged
                            </span>
                          ) : (
                            <span className="text-xs text-zinc-500">Normal</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => openNotesModal(stat)}
                              className="p-2 hover:bg-white/10 rounded transition-colors text-zinc-400 hover:text-white"
                              title="Add Notes"
                            >
                              <NotePencil size={16} />
                            </button>
                            {stat.flag_status === 'flagged' && (
                              <button
                                onClick={() => handleRemoveFlag(stat.user_id)}
                                className="p-2 hover:bg-green-500/10 rounded transition-colors text-zinc-400 hover:text-green-400"
                                title="Remove Flag"
                              >
                                <CheckCircle size={16} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {cancellationStats.length === 0 && (
                <div className="text-center py-12">
                  <ProhibitInset size={48} className="mx-auto text-zinc-600 mb-4" />
                  <p className="text-zinc-500">No cancellations found</p>
                </div>
              )}
            </div>

            {/* Policy Info */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <h3 className="text-white font-semibold mb-4">Automatic Flagging Rules</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="bg-[#0a0a0a] p-4 rounded">
                  <p className="text-zinc-400 mb-2">Users are <span className="text-red-400">automatically flagged</span> if:</p>
                  <ul className="text-zinc-500 space-y-1">
                    <li>• 2+ cancellations within 24 hours (last 30 days)</li>
                    <li>• 3+ cancellations within 48 hours (last 30 days)</li>
                  </ul>
                </div>
                <div className="bg-[#0a0a0a] p-4 rounded">
                  <p className="text-zinc-400 mb-2">Admin actions:</p>
                  <ul className="text-zinc-500 space-y-1">
                    <li>• Review cancellation patterns</li>
                    <li>• Remove flags manually</li>
                    <li>• Add notes for tracking</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notes Modal */}
        {showNotesModal && selectedUserForNotes && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
            <div className="bg-[#121212] border border-white/10 rounded-lg max-w-md w-full">
              <div className="p-6 border-b border-white/10">
                <h3 className="text-xl font-semibold text-white">Admin Notes</h3>
                <p className="text-zinc-500 text-sm mt-1">{selectedUserForNotes.user_name}</p>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <Label className="text-zinc-400 mb-2 block">Notes</Label>
                  <textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    className="w-full bg-[#0a0a0a] border border-white/10 text-white rounded-md p-3 text-sm"
                    placeholder="Add notes about this user's cancellation behavior..."
                    rows={4}
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowNotesModal(false)}
                    variant="outline"
                    className="flex-1 border-white/10 text-zinc-400 hover:text-white"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveNotes}
                    className="flex-1 bg-[#D4AF37] hover:bg-[#c9a032] text-black"
                  >
                    Save Notes
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Disputes Tab */}
        {activeTab === 'disputes' && (
          <DisputesTab token={token} />
        )}

        {/* Fees Tab */}
        {activeTab === 'fees' && (
          <div className="space-y-8">
            {/* Fee System Status & Revenue Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-[#121212] border border-white/10 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Gear size={24} className="text-[#D4AF37]" />
                    <h3 className="text-lg font-semibold text-white">Fee System</h3>
                  </div>
                  <button
                    data-testid="toggle-fee-system"
                    onClick={handleToggleFeeEnabled}
                    className={`px-3 py-1 text-sm rounded-full transition-colors ${
                      feeConfig?.enabled 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : 'bg-red-500/20 text-red-400 border border-red-500/30'
                    }`}
                  >
                    {feeConfig?.enabled ? 'Enabled' : 'Disabled'}
                  </button>
                </div>
                <p className="text-zinc-500 text-sm">
                  {feeConfig?.enabled 
                    ? 'Marketplace fees are being applied to all bookings' 
                    : 'Fees are currently disabled. No service fees or commissions will be applied.'}
                </p>
              </div>

              <div className="bg-[#121212] border border-white/10 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Wallet size={24} className="text-emerald-400" />
                  <h3 className="text-lg font-semibold text-white">Platform Revenue</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Total Revenue</span>
                    <span className="text-emerald-400 font-semibold">${platformRevenue?.total_platform_revenue?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-600">Guest Fees</span>
                    <span className="text-zinc-400">${platformRevenue?.total_guest_fees?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-600">Host Commissions</span>
                    <span className="text-zinc-400">${platformRevenue?.total_host_commissions?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
              </div>

              <div className="bg-[#121212] border border-white/10 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CurrencyDollar size={24} className="text-blue-400" />
                  <h3 className="text-lg font-semibold text-white">Pending Payouts</h3>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-zinc-500">Pending Amount</span>
                    <span className="text-blue-400 font-semibold">${pendingPayouts?.total_pending?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-600">Hosts Awaiting</span>
                    <span className="text-zinc-400">{pendingPayouts?.hosts?.length || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Fee Calculator / Preview */}
            <div className="bg-[#121212] border border-white/10 p-6">
              <div className="flex items-center gap-3 mb-6">
                <Calculator size={24} className="text-[#D4AF37]" />
                <h3 className="text-lg font-semibold text-white">Fee Calculator</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-zinc-400 mb-2 block">Test Booking Price ($)</Label>
                  <div className="flex gap-3">
                    <Input
                      data-testid="fee-test-price-input"
                      type="number"
                      value={testPrice}
                      onChange={(e) => setTestPrice(parseFloat(e.target.value) || 0)}
                      className="bg-[#0a0a0a] border-white/10 text-white w-32"
                      min="0"
                      step="5"
                    />
                    <Button
                      data-testid="calculate-fee-btn"
                      onClick={handlePreviewFee}
                      className="bg-[#D4AF37] hover:bg-[#c9a032] text-black"
                    >
                      Calculate
                    </Button>
                  </div>
                  <div className="flex gap-2 mt-3">
                    {[15, 25, 35, 60].map((price) => (
                      <button
                        key={price}
                        data-testid={`quick-calc-${price}`}
                        onClick={async () => {
                          setTestPrice(price);
                          try {
                            const res = await adminApi.previewFeeCalculation(price, token);
                            setFeePreview(res.data);
                          } catch (error) {
                            toast.error('Failed to preview fee');
                          }
                        }}
                        className="px-3 py-1 text-sm bg-white/5 hover:bg-white/10 text-zinc-400 rounded transition-colors"
                      >
                        ${price}
                      </button>
                    ))}
                  </div>
                </div>

                {feePreview && (
                  <div className="bg-[#0a0a0a] border border-white/5 p-4 rounded-lg">
                    <h4 className="text-white font-semibold mb-3">Fee Breakdown for ${feePreview.listing_price?.toFixed(2)}</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-zinc-500">Listing Price</span>
                        <span className="text-white">${feePreview.listing_price?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-zinc-500">+ Guest Service Fee</span>
                        <span className="text-blue-400">${feePreview.guest_service_fee?.toFixed(2)}</span>
                      </div>
                      <div className="border-t border-white/10 pt-2 flex justify-between font-semibold">
                        <span className="text-zinc-400">Guest Total</span>
                        <span className="text-[#D4AF37]">${feePreview.guest_total?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between mt-3 pt-2 border-t border-white/10">
                        <span className="text-zinc-500">- Host Commission</span>
                        <span className="text-red-400">-${feePreview.host_commission?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-semibold">
                        <span className="text-zinc-400">Host Payout</span>
                        <span className="text-green-400">${feePreview.host_payout?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between mt-3 pt-2 border-t border-white/10 font-semibold">
                        <span className="text-zinc-400">Platform Revenue</span>
                        <span className="text-emerald-400">${feePreview.platform_revenue?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Fee Tier Configuration */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Guest Fee Tiers */}
              <div className="bg-[#121212] border border-white/10 p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Guest Service Fee Tiers</h3>
                <p className="text-zinc-500 text-sm mb-4">Fees charged to guests on top of the listing price</p>
                
                <div className="space-y-3">
                  {feeConfig?.guest_fee_tiers?.map((tier, index) => (
                    <div key={index} className="bg-[#0a0a0a] border border-white/5 p-4 rounded">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-zinc-400">
                            ${tier.min_price} - {tier.max_price !== null ? `$${tier.max_price}` : '∞'}
                          </span>
                        </div>
                        <div className="text-[#D4AF37] font-semibold">
                          {tier.flat_fee !== null ? `$${tier.flat_fee.toFixed(2)} flat` : `${tier.percentage}%`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Host Commission Tiers */}
              <div className="bg-[#121212] border border-white/10 p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Host Commission Tiers</h3>
                <p className="text-zinc-500 text-sm mb-4">Commission deducted from host earnings</p>
                
                <div className="space-y-3">
                  {feeConfig?.host_commission_tiers?.map((tier, index) => (
                    <div key={index} className="bg-[#0a0a0a] border border-white/5 p-4 rounded">
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-zinc-400">
                            ${tier.min_price} - {tier.max_price !== null ? `$${tier.max_price}` : '∞'}
                          </span>
                        </div>
                        <div className="text-red-400 font-semibold">
                          {tier.flat_fee !== null ? `$${tier.flat_fee.toFixed(2)} flat` : `${tier.percentage}%`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4 justify-end">
              <Button
                data-testid="reset-fees-btn"
                onClick={handleResetFeeConfig}
                variant="outline"
                className="border-white/10 text-zinc-400 hover:text-white hover:bg-white/5"
              >
                <ArrowsClockwise size={18} className="mr-2" />
                Reset to Defaults
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Disputes Tab Component
function DisputesTab({ token }) {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [showResolveModal, setShowResolveModal] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    loadDisputes();
  }, [statusFilter]);

  const loadDisputes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/disputes${statusFilter !== 'all' ? `?status=${statusFilter}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDisputes(data);
      }
    } catch (error) {
      console.error('Failed to load disputes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (resolution) => {
    if (!showResolveModal || !resolutionNotes) return;
    
    setResolving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/disputes/${showResolveModal.id}/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          resolution,
          resolution_notes: resolutionNotes
        })
      });
      
      if (res.ok) {
        toast.success(`Dispute resolved: ${resolution === 'refund' ? 'Guest refunded' : 'No refund'}`);
        setShowResolveModal(null);
        setResolutionNotes('');
        loadDisputes();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to resolve dispute');
      }
    } catch (error) {
      toast.error('Failed to resolve dispute');
    } finally {
      setResolving(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'resolved_refund': return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'resolved_no_refund': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'expired': return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
      default: return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-serif text-white">Dispute Management</h2>
        <div className="flex gap-2">
          {['all', 'open', 'resolved_refund', 'resolved_no_refund'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                statusFilter === status
                  ? 'bg-[#D4AF37] text-black'
                  : 'bg-[#121212] text-zinc-400 hover:text-white border border-white/10'
              }`}
            >
              {status === 'all' ? 'All' : status.replace('_', ' ').replace('resolved ', '')}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : disputes.length === 0 ? (
        <div className="text-center py-16 bg-[#121212] border border-white/10">
          <Flag size={48} className="mx-auto text-zinc-600 mb-4" weight="duotone" />
          <p className="text-zinc-500">No disputes found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {disputes.map((dispute) => (
            <div key={dispute.id} className="bg-[#121212] border border-white/10 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-white font-semibold text-lg">{dispute.listing_title}</h3>
                  <p className="text-zinc-500 text-sm">
                    Booking: {dispute.booking_date} • Amount: ${dispute.booking_amount?.toFixed(2)}
                  </p>
                </div>
                <span className={`px-3 py-1 text-xs border rounded-full ${getStatusColor(dispute.status)}`}>
                  {dispute.status.replace('_', ' ')}
                </span>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="bg-[#0a0a0a] p-4 rounded">
                  <p className="text-zinc-500 text-xs mb-1">Guest</p>
                  <p className="text-white">{dispute.guest_info?.name || dispute.guest_name}</p>
                  <p className="text-zinc-400 text-sm">{dispute.guest_info?.email}</p>
                </div>
                <div className="bg-[#0a0a0a] p-4 rounded">
                  <p className="text-zinc-500 text-xs mb-1">Host</p>
                  <p className="text-white">{dispute.host_info?.name}</p>
                  <p className="text-zinc-400 text-sm">{dispute.host_info?.email}</p>
                </div>
              </div>
              
              <div className="bg-[#0a0a0a] p-4 rounded mb-4">
                <p className="text-zinc-500 text-xs mb-1">Reason: {dispute.reason?.replace(/_/g, ' ')}</p>
                <p className="text-white">{dispute.description}</p>
                <p className="text-zinc-600 text-xs mt-2">Filed: {new Date(dispute.created_at).toLocaleString()}</p>
              </div>
              
              {dispute.status === 'open' && (
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowResolveModal(dispute)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    Resolve with Refund
                  </Button>
                  <Button
                    onClick={() => setShowResolveModal({ ...dispute, defaultResolution: 'no_refund' })}
                    className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                  >
                    Resolve - No Refund
                  </Button>
                </div>
              )}
              
              {dispute.resolved_at && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <p className="text-zinc-500 text-sm">
                    Resolved: {new Date(dispute.resolved_at).toLocaleString()}
                  </p>
                  {dispute.resolution_notes && (
                    <p className="text-zinc-400 text-sm mt-1">Notes: {dispute.resolution_notes}</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Resolve Modal */}
      {showResolveModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
          <div className="bg-[#121212] border border-white/10 rounded-lg max-w-lg w-full">
            <div className="p-6 border-b border-white/10">
              <h3 className="text-xl font-semibold text-white">Resolve Dispute</h3>
              <p className="text-zinc-500 text-sm mt-1">{showResolveModal.listing_title}</p>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-[#0a0a0a] p-4 rounded">
                <p className="text-zinc-400 text-sm mb-2">Dispute Details:</p>
                <p className="text-white">{showResolveModal.description}</p>
                <p className="text-zinc-500 text-sm mt-2">
                  Host Payout Amount: ${showResolveModal.host_payout?.toFixed(2) || '0.00'}
                </p>
              </div>
              
              <div>
                <label className="text-zinc-400 mb-2 block text-sm">Resolution Notes *</label>
                <textarea
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                  placeholder="Explain the resolution decision..."
                  className="w-full bg-[#0a0a0a] border border-white/10 text-white p-3 rounded"
                  rows={3}
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={() => {
                    setShowResolveModal(null);
                    setResolutionNotes('');
                  }}
                  variant="outline"
                  className="flex-1 border-white/10 text-zinc-400"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleResolve('no_refund')}
                  disabled={resolving || !resolutionNotes}
                  className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white"
                >
                  No Refund
                </Button>
                <Button
                  onClick={() => handleResolve('refund')}
                  disabled={resolving || !resolutionNotes}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  Issue Refund
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
